import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { 
  ShieldCheck, 
  Scale, 
  Briefcase, 
  Calculator, 
  Server,
  ArrowRight,
  Building2,
  Zap,
  Factory,
  HardHat,
  Globe,
  Monitor,
  Users,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_compliance-hub-324/artifacts/hzwwrgat_Home_Page.png";
const CONSTRUCTION_IMAGE = "https://customer-assets.emergentagent.com/job_compliance-hub-324/artifacts/hzwwrgat_Home_Page.png";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 1, 0.5, 1] }
  }
};

const serviceIcons = {
  ehs: ShieldCheck,
  risk: Scale,
  pmo: Briefcase,
  payroll: Calculator,
  it: Server
};

const industryIcons = [Building2, HardHat, Zap, Factory, Globe, Monitor, Users, Server];

export default function Home() {
  const { t, language } = useLanguage();

  const services = [
    { key: 'ehs', icon: serviceIcons.ehs, color: 'bg-secondary/10 text-secondary' },
    { key: 'risk', icon: serviceIcons.risk, color: 'bg-primary/10 text-primary' },
    { key: 'pmo', icon: serviceIcons.pmo, color: 'bg-accent/20 text-accent-foreground' },
    { key: 'payroll', icon: serviceIcons.payroll, color: 'bg-secondary/10 text-secondary' },
    { key: 'it', icon: serviceIcons.it, color: 'bg-primary/10 text-primary' }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${HERO_IMAGE})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/80 to-primary/40" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="text-white"
            >
              {/* Tagline Badge */}
              <motion.div 
                variants={itemVariants}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 mb-8"
              >
                <span className="text-secondary font-semibold text-sm tracking-wider uppercase">
                  {t.hero.tagline}
                </span>
              </motion.div>

              {/* Headline */}
              <motion.h1 
                variants={itemVariants}
                className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 font-heading uppercase tracking-tight"
              >
                {t.hero.headline}
              </motion.h1>

              {/* Subheadline */}
              <motion.p 
                variants={itemVariants}
                className="text-lg md:text-xl text-white/80 leading-relaxed mb-8 max-w-xl"
              >
                {t.hero.subheadline}
              </motion.p>

              {/* CTAs */}
              <motion.div 
                variants={itemVariants}
                className="flex flex-wrap gap-4"
              >
                <Link to="/contact">
                  <Button 
                    size="lg"
                    className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-none px-8 py-6 text-base font-semibold uppercase tracking-wide"
                    data-testid="hero-cta-primary"
                  >
                    {t.hero.cta}
                    <ArrowRight className="ms-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button 
                    size="lg"
                    variant="outline"
                    className="bg-transparent border-2 border-white text-white hover:bg-white/10 rounded-none px-8 py-6 text-base font-semibold uppercase tracking-wide"
                    data-testid="hero-cta-secondary"
                  >
                    {t.hero.learnMore}
                  </Button>
                </Link>
              </motion.div>

              {/* Trust Badges */}
              <motion.div 
                variants={itemVariants}
                className="flex flex-wrap items-center gap-6 mt-12 pt-8 border-t border-white/20"
              >
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-6 h-6 text-secondary" />
                  <span className="text-sm font-medium">ISO 45001</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-6 h-6 text-secondary" />
                  <span className="text-sm font-medium">ISO 14001</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="w-6 h-6 text-secondary" />
                  <span className="text-sm font-medium">{language === 'en' ? 'Global Operations' : 'عمليات عالمية'}</span>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="hidden lg:block"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-secondary/20 blur-3xl" />
                <img 
                  src={CONSTRUCTION_IMAGE}
                  alt="Safety Professional"
                  className="relative w-full h-[500px] object-cover shadow-2xl"
                  style={{ clipPath: 'polygon(0 0, 100% 0, 100% 90%, 90% 100%, 0 100%)' }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 bg-background" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-primary uppercase tracking-tight mb-4">
              {t.services.title}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t.services.subtitle}
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {services.map((service, index) => {
              const Icon = service.icon;
              const serviceData = t.services[service.key];
              return (
                <motion.div key={service.key} variants={itemVariants}>
                  <Card 
                    className="h-full border-border hover:border-primary/50 transition-all duration-300 rounded-none group hover:shadow-xl"
                    data-testid={`service-card-${service.key}`}
                  >
                    <CardContent className="p-8">
                      <div className={`w-14 h-14 ${service.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className="w-7 h-7" />
                      </div>
                      <h3 className="text-xl font-bold text-primary mb-3 uppercase tracking-wide">
                        {serviceData.shortTitle}
                      </h3>
                      <p className="text-muted-foreground mb-6 leading-relaxed">
                        {serviceData.description}
                      </p>
                      <Link 
                        to={`/services#${service.key}`}
                        className="inline-flex items-center text-secondary font-semibold hover:gap-3 transition-all duration-200 gap-2 text-sm uppercase tracking-wide"
                      >
                        {t.services.learnMore}
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/services">
              <Button 
                size="lg"
                variant="outline"
                className="rounded-none border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground px-8 py-6 font-semibold uppercase tracking-wide"
                data-testid="view-all-services"
              >
                {t.services.viewAll}
                <ArrowRight className="ms-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-24 bg-muted" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-primary uppercase tracking-tight mb-4">
              {t.industries.title}
            </h2>
            <p className="text-lg text-muted-foreground">
              {t.industries.subtitle}
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4"
          >
            {t.industries.list.map((industry, index) => {
              const Icon = industryIcons[index % industryIcons.length];
              return (
                <motion.div 
                  key={industry} 
                  variants={itemVariants}
                  className="bg-card p-6 text-center hover:shadow-lg transition-all duration-300 border border-border hover:border-secondary group"
                  data-testid={`industry-${index}`}
                >
                  <Icon className="w-8 h-8 mx-auto mb-4 text-muted-foreground group-hover:text-secondary transition-colors duration-300" />
                  <span className="text-sm font-semibold text-primary uppercase tracking-wide">
                    {industry}
                  </span>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-primary-foreground uppercase tracking-tight mb-6">
              {t.cta.title}
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-10 max-w-2xl mx-auto">
              {t.cta.subtitle}
            </p>
            <Link to="/contact">
              <Button 
                size="lg"
                className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-none px-10 py-6 text-lg font-semibold uppercase tracking-wide"
                data-testid="cta-button"
              >
                {t.cta.button}
                <ArrowRight className="ms-2 w-6 h-6" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
